import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import * as T from '../types';
import { ItemChoice, ItemPicker } from '../common';
import { SECTIONS, UNITS, number, parseNumber } from '../labels';
import { IngredientLine, catalogSubtitle } from './recipeText';

/**
 * Edits one ingredient line of the recipe being edited: an existing food item or a new name
 * (created by the server when the recipe is saved). `line` null means a new line.
 */
export function IngredientDialog({ open, line, items, onClose, onSave, onRemove }: {
  open: boolean;
  line: IngredientLine | null;
  items: T.CatalogItem[];
  onClose: () => void;
  onSave: (line: IngredientLine) => void;
  onRemove: () => void;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [choice, setChoice] = useState<ItemChoice | null>(null);
  const [section, setSection] = useState<T.StoreSection>('Other');
  const [quantityText, setQuantityText] = useState('');
  const [unit, setUnit] = useState<T.MeasureUnit | ''>('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (!line) {
      setChoice(null);
      setSection('Other');
      setQuantityText('');
      setUnit('');
      setNote('');
      return;
    }
    const item = line.catalogItemPublicId ? items.find(i => i.publicId === line.catalogItemPublicId) : undefined;
    setChoice(item ? { item } : { newName: line.name });
    setSection(line.newItemSection ?? 'Other');
    setQuantityText(line.quantity !== null && line.quantity !== undefined ? number(line.quantity) : '');
    setUnit(line.unit ?? '');
    setNote(line.note ?? '');
  }, [open, line, items]);

  const choose = (next: ItemChoice | null) => {
    setChoice(next);
    setError(null);
    // The item's usual unit when nothing was written yet
    if (next && 'item' in next && quantityText.trim() === '' && unit === '') setUnit(next.item.defaultUnit);
  };

  const save = () => {
    if (!choice) { setError('Escolhe ou cria o artigo.'); return; }
    let quantity: number | null = null;
    if (quantityText.trim() !== '') {
      quantity = parseNumber(quantityText);
      if (quantity === null || quantity <= 0) { setError('A quantidade deve ser um número positivo.'); return; }
    }
    const isNew = 'newName' in choice;
    onSave({
      key: line?.key ?? '',
      catalogItemPublicId: isNew ? null : choice.item.publicId,
      name: isNew ? choice.newName.trim() : choice.item.name,
      newItemSection: isNew ? section : null,
      quantity,
      unit: unit === '' ? null : unit,
      note: note.trim() === '' ? null : note.trim(),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} fullWidth maxWidth="sm">
      <DialogTitle>Ingrediente</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Box>
            <ItemPicker value={choice} onChange={choose} items={items} label="Artigo" autoFocus={!line} />
            {choice && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 1.5 }}>
                {'item' in choice ? catalogSubtitle(choice.item) : 'Artigo novo: é criado no catálogo ao guardar a receita.'}
              </Typography>
            )}
          </Box>
          {choice && 'newName' in choice && (
            <TextField select label="Secção do supermercado" value={section} onChange={e => setSection(e.target.value as T.StoreSection)}>
              {SECTIONS.map(s => <MenuItem key={s.value} value={s.value}>{s.text}</MenuItem>)}
            </TextField>
          )}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Quantidade"
              placeholder="Opcional"
              value={quantityText}
              onChange={e => setQuantityText(e.target.value)}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              sx={{ flex: 1 }}
            />
            <TextField select label="Unidade" value={unit} onChange={e => setUnit(e.target.value as T.MeasureUnit | '')} sx={{ flex: 1 }}>
              <MenuItem value="">—</MenuItem>
              {UNITS.map(u => <MenuItem key={u.value} value={u.value}>{u.text}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField
            label="Nota"
            placeholder="Opcional (ex.: picada, q.b.)"
            value={note}
            onChange={e => setNote(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {line && <Button color="error" onClick={onRemove} sx={{ mr: 'auto' }}>Retirar da receita</Button>}
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save}>OK</Button>
      </DialogActions>
    </Dialog>
  );
}
